import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { get, post } from '../../services/ApiService';
import { Filter, Eye, Download, Check, Edit2, Trash2, Plus, FileText, CheckCircle, XCircle } from 'react-feather';
import { useWindowManager } from '../Window/WindowManager';
import { useNotification } from './NotificationProvider';
import { resolveRoute, parseRoute } from './ComponentRegistry';
import componentRegistry from './ComponentRegistry';
import { resolveWindowSize } from '../Window/windowTypes';
import Overlay from '../Overlay';
import './Grid.css';
import { GridField, GridAction, GridFilter, GridProps } from './Grid/gridTypes';
import { GridRow, GridFilters, GridActions, GridSelectionActions, GridFooter } from './Grid/index';
import { OPERATORS, API_ENDPOINT, ROW_HEIGHT, HEADER_HEIGHT, SAFETY_MARGIN, convertBackendInitialFilter } from './Grid/gridConstants';
import { createCellRenderers } from './Grid/cellRenderers';
import { useDocumentPreviewHandler } from '../../hooks/useDocumentPreviewHandler';
import { useIframePreviewHandler } from '../../hooks/useIframePreviewHandler';
import { useLayout } from '../Layout';
import { useDebounce } from './Grid/hooks/useDebounce';
import { SortItem } from './Grid/hooks/useGridSort';

export default function Grid({
    router,
    onSelect,
    extraData = {},
    initialFilter = [],
    actions: propActions = [],
    hideDefaultActions = false,
    selectionMode = 'multiple',
    triggerClose,
    triggerSelect,
}: GridProps) {
    const windowManager = useWindowManager();
    const notification = useNotification();
    const layout = useLayout() as { openMenu: (item: any, forceNew?: boolean) => void } | null;
    const { openDocumentPreview } = useDocumentPreviewHandler();
    const { openIframePreview } = useIframePreviewHandler();
    const cellRenderers = useMemo(() => createCellRenderers({ openDocumentPreview, openIframePreview }), [openDocumentPreview, openIframePreview]);

    // Core state
    const [fields, setFields] = useState<GridField[]>([]);
    const fieldsRef = useRef<GridField[]>([]);
    fieldsRef.current = fields;
    const [rows, setRows] = useState<any[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [isReady, setIsReady] = useState(false);
    const [refreshCounter, setRefreshCounter] = useState(0);
    const [rowHeight, setRowHeight] = useState(ROW_HEIGHT);

    // Pagination state
    const [page, setPage] = useState(1);
    const [pageInput, setPageInput] = useState(1);
    const [pageSize, setPageSize] = useState<number | 'auto'>('auto');
    const [autoPageSize, setAutoPageSize] = useState<number | null>(null);
    const [wrapperWidth, setWrapperWidth] = useState(0);

    // Filter state
    const normalizedInitialFilter = Array.isArray(initialFilter)
        ? initialFilter
        : convertBackendInitialFilter(initialFilter);
    const [pendingFilters, setPendingFilters] = useState<GridFilter[]>(normalizedInitialFilter);
    const [appliedFilters, setAppliedFilters] = useState<GridFilter[]>(normalizedInitialFilter);
    const lastFilterInputRef = useRef<HTMLInputElement | HTMLSelectElement | null>(null);

    // Sort state
    const [sort, setSort] = useState<SortItem[]>([]);

    // Selection state
    const [selectedRows, setSelectedRows] = useState<Record<string | number, any>>({});

    // Column state
    const [visibleColumns, setVisibleColumns] = useState<Record<string, boolean>>({});
    const [showColumnSelector, setShowColumnSelector] = useState(false);
    const [columnWidths, setColumnWidths] = useState<Record<string, number>>({});


    // Persistence state
    const [hasSavedState, setHasSavedState] = useState(false);
    const [backendActions, setBackendActions] = useState<GridAction[]>([]);

    // Refs
    const fetchIdRef = useRef(0);
    const initialStateRef = useRef<{
        filters: GridFilter[];
        sort: SortItem[];
        visibleColumns: Record<string, boolean>;
    }>({ filters: [], sort: [], visibleColumns: {} });
    const resizingRef = useRef<{ field: string; startX: number; startWidth: number } | null>(null);
    const tableWrapperRef = useRef<HTMLDivElement>(null);
    const lastAutoPageSizeRef = useRef(0);

    const storageKey = `grid_state_${router}`;
    const debouncedAutoPageSize = useDebounce(autoPageSize, 300);
    const effectivePageSize = pageSize === 'auto' ? (debouncedAutoPageSize || 15) : pageSize;
    const totalPages = useMemo(() => Math.max(1, Math.ceil(total / effectivePageSize)), [total, effectivePageSize]);
    const debouncedPageInput = useDebounce(pageInput, 300);

    useEffect(() => {
        if (debouncedPageInput !== page) {
            setPage(debouncedPageInput);
        }
    }, [debouncedPageInput]);

    // Auto page size: mede as alturas reais do thead e da primeira linha no DOM;
    // rowHeight configurado é só fallback enquanto não há linhas renderizadas
    const calculateLayout = useCallback(() => {
        const wrapper = tableWrapperRef.current;
        if (!wrapper) return;

        setWrapperWidth(wrapper.clientWidth);

        const thead = wrapper.querySelector('thead');
        const headerHeight = thead ? thead.getBoundingClientRect().height : HEADER_HEIGHT;
        const firstRow = wrapper.querySelector('tbody tr.grid-row');
        const realRowHeight = firstRow ? firstRow.getBoundingClientRect().height : rowHeight;

        const availableHeight = wrapper.clientHeight - headerHeight - SAFETY_MARGIN;
        if (availableHeight > 0 && realRowHeight > 0) {
            const calculatedSize = Math.max(10, Math.floor(availableHeight / realRowHeight));
            if (calculatedSize !== lastAutoPageSizeRef.current) {
                lastAutoPageSizeRef.current = calculatedSize;
                setAutoPageSize(calculatedSize);
            }
        }
    }, [rowHeight]);

    useEffect(() => {
        const resizeObserver = new ResizeObserver(() => {
            requestAnimationFrame(() => calculateLayout());
        });

        if (tableWrapperRef.current) {
            resizeObserver.observe(tableWrapperRef.current);
        }

        const timeoutId = setTimeout(calculateLayout, 50);
        return () => {
            resizeObserver.disconnect();
            clearTimeout(timeoutId);
        };
    }, [calculateLayout]);

    // Recalcula quando as linhas mudam: a altura real da linha e a scrollbar
    // vertical só existem com dados renderizados no DOM
    useEffect(() => {
        calculateLayout();
    }, [rows, calculateLayout]);

    const extraDataString = JSON.stringify(extraData);
    const stableExtraData = useMemo(() => extraData, [extraDataString]);

    // Initialize grid
    useEffect(() => {
        let cancelled = false;
        const init = async () => {
            try {
                const saved = localStorage.getItem(storageKey);
                let savedState: any = null;
                if (saved) {
                    try {
                        savedState = JSON.parse(saved);
                        setHasSavedState(true);
                    } catch {}
                }

                const response = await get(`/?route=${router}/grid&action=actionGetGridParams&extraData=${JSON.stringify(extraData)}`);
                if (cancelled) return;

                if (response.data) {
                    const fieldList = response.data.fieldList || [];
                    setFields(fieldList);

                    if (response.data.rowHeight && typeof response.data.rowHeight === 'number') {
                        setRowHeight(response.data.rowHeight);
                    }

                    if (response.data.actionList && Array.isArray(response.data.actionList)) {
                        setBackendActions(response.data.actionList);
                    }

                    if (savedState?.sort) {
                        setSort(savedState.sort);
                    } else if (response.data.orderList?.length > 0) {
                        const convertedSort = response.data.orderList.map((s: any) => ({
                            name: s.name,
                            dir: (s.dir === 1 || s.dir === 'asc') ? 'asc' as const : 'desc' as const
                        }));
                        setSort(convertedSort);
                    }

                    const backendFilters = response.data.initialFilterList && Object.keys(response.data.initialFilterList).length > 0
                        ? convertBackendInitialFilter(response.data.initialFilterList)
                        : [];

                    if (savedState?.filters) {
                        setPendingFilters(savedState.filters);
                        setAppliedFilters(savedState.filters);
                    } else {
                        const merged = [
                            ...normalizedInitialFilter,
                            ...backendFilters.filter(bf => !normalizedInitialFilter.some(pf => pf.name === bf.name))
                        ];
                        if (merged.length > 0) {
                            setPendingFilters(merged);
                            setAppliedFilters(merged);
                        }
                    }

                    if (savedState?.pageSize) {
                        setPageSize(savedState.pageSize);
                    }

                    if (savedState?.columnWidths) {
                        setColumnWidths(savedState.columnWidths);
                    }

                    const visible: Record<string, boolean> = {};
                    const defaultVisible: Record<string, boolean> = {};
                    fieldList.forEach((f: GridField) => {
                        visible[f.name] = f.visible !== false;
                        defaultVisible[f.name] = f.visible !== false;
                    });
                    if (savedState?.visibleColumns) {
                        Object.assign(visible, savedState.visibleColumns);
                    }
                    setVisibleColumns(visible);

                    const backendSort = response.data.orderList?.length > 0
                        ? response.data.orderList.map((s: any) => ({
                            name: s.name,
                            dir: (s.dir === 1 || s.dir === 'asc') ? 'asc' as const : 'desc' as const
                        }))
                        : [];
                    initialStateRef.current = {
                        filters: [
                            ...normalizedInitialFilter,
                            ...backendFilters.filter(bf => !normalizedInitialFilter.some(pf => pf.name === bf.name))
                        ],
                        sort: backendSort,
                        visibleColumns: defaultVisible,
                    };

                    setIsReady(true);
                }
            } catch (error) {
                console.error('Error initializing grid:', error);
            }
        };
        init();
        return () => { cancelled = true; };
    }, [router, storageKey]);

    // Fetch data
    useEffect(() => {
        if (!isReady || fieldsRef.current.length === 0) return;
        if (pageSize === 'auto' && !debouncedAutoPageSize) return;

        const fetchId = ++fetchIdRef.current;
        setLoading(true);

        const skip = (page - 1) * effectivePageSize;
        const normalizeNumber = (v: string | undefined) =>
            v ? v.replace(',', '.') : v;

        const filterValue = appliedFilters.map(f => {
            const field = fieldsRef.current.find(fld => fld.name === f.name);
            let value: string | string[] = f.value;
            let value2 = f.value2;
            if (field?.multiple && f.value && typeof f.value === 'string' && f.value.includes(',')) {
                value = f.value.split(',');
            } else if (field?.type === 'number' && typeof value === 'string') {
                value = normalizeNumber(value) ?? value;
                value2 = normalizeNumber(value2);
            }
            return {
                name: f.name,
                operator: f.operator,
                value,
                value2,
                type: field?.type || 'string'
            };
        });
        const url = `/?route=${router}/grid&action=list&skip=${skip}&limit=${effectivePageSize}&filterValue=${JSON.stringify(filterValue)}&sort=${JSON.stringify(sort)}&extraData=${JSON.stringify(stableExtraData)}`;

        get(url).then(response => {
            if (fetchId !== fetchIdRef.current) return;
            if (response.data?.error) {
                notification.error(response.data.error);
            }
            if (response.data) {
                const newRows = response.data.rows || [];
                setRows(newRows);
                setTotal(response.data.total || 0);
                setSelectedRows(prev => {
                    const hasSelected = Object.keys(prev).length > 0;
                    if (!hasSelected) return prev;
                    const updated: Record<string | number, any> = {};
                    for (const id of Object.keys(prev)) {
                        const fresh = newRows.find((r: any) => String(r.id) === id);
                        updated[id] = fresh ?? prev[id];
                    }
                    return updated;
                });
            }
        }).catch(error => {
            console.error('Error fetching data:', error);
            notification.error('Erro ao carregar dados: ' + (error?.message || 'falha na comunicação'));
        }).finally(() => {
            if (fetchId === fetchIdRef.current) {
                setLoading(false);
            }
        });
    }, [isReady, router, page, effectivePageSize, appliedFilters, sort, stableExtraData, refreshCounter]); // eslint-disable-line react-hooks/exhaustive-deps

    const refreshData = useCallback(() => {
        setAppliedFilters(pendingFilters);
        setRefreshCounter(c => c + 1);
    }, [pendingFilters]);

    // Refresh filterLists on data refresh so newly created related entities appear correctly
    useEffect(() => {
        if (refreshCounter === 0) return;
        get(`/?route=${router}/grid&action=actionGetGridParams&extraData=${JSON.stringify(stableExtraData)}`)
            .then(response => {
                if (!response.data?.fieldList) return;
                setFields(prev => prev.map(f => {
                    const updated = (response.data.fieldList as GridField[]).find((nf: GridField) => nf.name === f.name);
                    return updated?.filterList ? { ...f, filterList: updated.filterList } : f;
                }));
            })
            .catch(() => {});
    }, [refreshCounter, router, stableExtraData]); // eslint-disable-line react-hooks/exhaustive-deps
    const visibleFields = useMemo(() => fields.filter(f => visibleColumns[f.name] !== false), [fields, visibleColumns]);
    const filtrableFields = useMemo(() => fields.filter(f => f.filtrable !== false), [fields]);
    const allActions = useMemo(() => {
        const merged = Array.isArray(backendActions) ? [...backendActions] : [];
        propActions.forEach((pa: any) => {
            if (typeof pa === 'object' && pa.route && !merged.find(a => a.route === pa.route)) {
                merged.push(pa);
            }
        });
        return merged;
    }, [backendActions, propActions]);

    const selectedRowsArray = useMemo(() => Object.values(selectedRows), [selectedRows]);
    const firstSelectedRow = selectedRowsArray[0];

    // Filter handlers
    const addFilter = useCallback((fieldName: string) => {
        const field = fields.find(f => f.name === fieldName);
        if (!field) return;
        const defaultOperator =
            field.type === 'string' || field.type === 'text' ? 'contains' :
            field.type === 'date' || field.type === 'datetime' ? 'gte' : 'eq';
        setPendingFilters(prev => [...prev, { name: fieldName, operator: defaultOperator, value: '' }]);
        setTimeout(() => lastFilterInputRef.current?.focus(), 0);
    }, [fields]);

    const removeFilter = useCallback((index: number) => {
        setPendingFilters(prev => {
            const newFilters = prev.filter((_, i) => i !== index);
            setAppliedFilters(newFilters);
            return newFilters;
        });
    }, []);

    const updateFilter = useCallback((index: number, updates: Partial<GridFilter>) => {
        setPendingFilters(prev => prev.map((f, i) => i === index ? { ...f, ...updates } : f));
    }, []);

    const applyFilters = useCallback(() => {
        setAppliedFilters(pendingFilters);
        setPage(1);
        setPageInput(1);
    }, [pendingFilters]);

    const handleFilterKeyDown = useCallback((e: React.KeyboardEvent) => {
        if (e.key === 'Enter') applyFilters();
    }, [applyFilters]);

    // Sort handler
    const handleSort = useCallback((fieldName: string) => {
        setSort(prev => {
            const existing = prev.find(s => s.name === fieldName);
            if (existing) {
                return existing.dir === 'asc'
                    ? prev.map(s => s.name === fieldName ? { ...s, dir: 'desc' as const } : s)
                    : prev.filter(s => s.name !== fieldName);
            }
            return [{ name: fieldName, dir: 'asc' as const }];
        });
    }, []);

    const getSortIndicator = useCallback((fieldName: string) => {
        const sortItem = sort.find(s => s.name === fieldName);
        if (!sortItem) return null;
        return sortItem.dir === 'asc' ? ' ▲' : ' ▼';
    }, [sort]);

    // Selection handlers
    const handleRowClick = useCallback((row: any, e?: React.MouseEvent) => {
        const isCtrlPressed = e?.ctrlKey || e?.metaKey;
        setSelectedRows(prev => {
            if (selectionMode === 'single') {
                if (prev[row.id] && Object.keys(prev).length === 1) {
                    onSelect?.([]);
                    return {};
                }
                onSelect?.([row]);
                return { [row.id]: row };
            }
            if (isCtrlPressed && selectionMode === 'multiple') {
                const newSelected = { ...prev };
                if (newSelected[row.id]) {
                    delete newSelected[row.id];
                } else {
                    newSelected[row.id] = row;
                }
                onSelect?.(Object.values(newSelected));
                return newSelected;
            } else {
                if (prev[row.id] && Object.keys(prev).length === 1) {
                    onSelect?.([]);
                    return {};
                }
                onSelect?.([row]);
                return { [row.id]: row };
            }
        });
    }, [onSelect, selectionMode]);

    const handleCheckboxChange = useCallback((row: any) => {
        if (selectionMode === 'none') return;
        if (selectionMode === 'single') {
            setSelectedRows(prev => {
                if (prev[row.id]) {
                    onSelect?.([]);
                    return {};
                }
                onSelect?.([row]);
                return { [row.id]: row };
            });
            return;
        }
        setSelectedRows(prev => {
            const newSelected = { ...prev };
            if (newSelected[row.id]) {
                delete newSelected[row.id];
            } else {
                newSelected[row.id] = row;
            }
            onSelect?.(Object.values(newSelected));
            return newSelected;
        });
    }, [onSelect, selectionMode]);

    const handleSelectAll = useCallback(() => {
        const allSelected = rows.length > 0 && rows.every(row => selectedRows[row.id]);
        if (allSelected) {
            setSelectedRows({});
            onSelect?.([]);
        } else {
            const newSelected: Record<string | number, any> = {};
            rows.forEach(row => { newSelected[row.id] = row; });
            setSelectedRows(newSelected);
            onSelect?.(rows);
        }
    }, [rows, selectedRows, onSelect]);

    // Persistence handlers
    const saveState = useCallback(() => {
        localStorage.setItem(storageKey, JSON.stringify({ filters: appliedFilters, visibleColumns, pageSize, sort, columnWidths }));
        setHasSavedState(true);
    }, [storageKey, appliedFilters, visibleColumns, pageSize, sort, columnWidths]);

    const clearSavedState = useCallback(() => {
        localStorage.removeItem(storageKey);
        setHasSavedState(false);
        const { filters, sort: initSort, visibleColumns: initVisible } = initialStateRef.current;
        setPendingFilters(filters);
        setAppliedFilters(filters);
        setSort(initSort);
        setVisibleColumns(initVisible);
        setColumnWidths({});
        setPageSize('auto');
        setPage(1);
        setPageInput(1);
    }, [storageKey]);

    // Action handlers
    const getActionIcon = useCallback((action: GridAction) => {
        if (action.icon) {
            const icons: Record<string, any> = { Plus, Edit2, Trash2, Eye, Download, Check, FileText, CheckCircle, XCircle };
            const Icon = icons[action.icon];
            return Icon ? <Icon size={14} /> : null;
        }
        if (!action.route) return null;
        try {
            const { action: actionType } = parseRoute(action.route);
            switch (actionType) {
                case 'add': return <Plus size={14} />;
                case 'edit': return <Edit2 size={14} />;
                case 'delete': return <Trash2 size={14} />;
                case 'view': case 'list': return <Eye size={14} />;
                default: return null;
            }
        } catch {
            return null;
        }
    }, []);

    const getActionClass = useCallback((action: GridAction) => {
        if (action.class) return action.class;
        if (!action.route) return '';
        try {
            const { action: actionType } = parseRoute(action.route);
            switch (actionType) {
                case 'add': return 'grid-action-add';
                case 'edit': return 'grid-action-edit';
                case 'delete': return 'grid-action-delete';
                default: return '';
            }
        } catch {
            return '';
        }
    }, []);

    const isActionEnabled = useCallback((action: GridAction): boolean => {
        const { requiresSelection, rowCondition } = action;
        if (requiresSelection === 'single' && selectedRowsArray.length !== 1) return false;
        if ((requiresSelection === 'multiple' || requiresSelection === true) && selectedRowsArray.length === 0) return false;
        if (rowCondition && firstSelectedRow) {
            try {
                // eslint-disable-next-line no-new-func
                return new Function('row', `return (${rowCondition})`)(firstSelectedRow);
            } catch { return false; }
        }
        return true;
    }, [selectedRowsArray.length, firstSelectedRow]);

    const handleAction = useCallback(async (action: GridAction, directRow?: any) => {
        const { confirm: confirmMessage, params = {} } = action;

        if (action.trigger) {
            const { handler, params: triggerParams = {} } = action.trigger;

            if (handler === 'openGridTab') {
                const { router: gridRouter, title, rowField, filterField, hideDefaultActions = false, modal = false, windowSize: gridWindowSize } = triggerParams;
                const effectiveRow = directRow || firstSelectedRow;
                const initialFilter = effectiveRow && rowField && filterField
                    ? [{ name: filterField, operator: 'eq', value: String(effectiveRow[rowField]) }]
                    : [];

                // Grid dependente de um pai (transição da etapa, por exemplo) cabe
                // melhor em modal que em aba, porque se fecha e volta para o pai.
                if (modal) {
                    windowManager.openWindow({
                        title: title || gridRouter,
                        component: Grid,
                        size: resolveWindowSize(gridWindowSize ?? 'xl'),
                        props: { router: gridRouter, initialFilter, selectionMode: 'multiple', hideDefaultActions }
                    });
                    return;
                }

                layout?.openMenu({
                    name: title || gridRouter,
                    icon: null,
                    component: Grid,
                    props: { router: gridRouter, initialFilter, selectionMode: 'none', hideDefaultActions }
                }, true);
                return;
            }

            if (handler === 'customAction') {
                const { url, action: customActionName, method = 'POST', confirm: confirmMsg, successMessage = 'Ação executada com sucesso!' } = triggerParams;
                const effectiveRow = directRow || firstSelectedRow;

                if (confirmMsg) {
                    const confirmed = await notification.confirm(confirmMsg);
                    if (!confirmed) return;
                }

                const loadingId = notification.showLoading('Executando...');
                try {
                    const apiUrl = `/?route=${url}&action=${customActionName}&rows=${encodeURIComponent(JSON.stringify([effectiveRow]))}`;
                    const apiCall = method.toUpperCase() === 'GET' ? get : post;
                    const response = await apiCall(apiUrl, {});
                    notification.hideLoading(loadingId);
                    if (response.data?.success) {
                        notification.success(response.data?.message || successMessage);
                        refreshData();
                    } else {
                        notification.error(response.data?.message || 'Erro ao executar ação');
                    }
                } catch (error: any) {
                    notification.hideLoading(loadingId);
                    notification.error(error.message || 'Erro de comunicação com o servidor');
                }
                return;
            }

            if (handler === 'openForm') {
                const { formKey, mode, title: formTitle, passSelectedRow = false } = triggerParams;

                if (!mode || (mode !== 'add' && mode !== 'edit')) {
                    console.warn(`openForm: 'mode' é obrigatório e deve ser 'add' ou 'edit'`);
                    return;
                }

                const formEntry = componentRegistry[formKey];
                if (!formEntry?.form) {
                    console.warn(`openForm: nenhum form registrado para formKey: ${formKey}`);
                    return;
                }

                const FormComponent = formEntry.form;
                const windowSize = resolveWindowSize(formEntry.windowSize ?? 'lg');
                let windowId: string;

                if (mode === 'add') {
                    const { prepareUrl, saveRoute } = triggerParams;

                    let initialData: any = null;
                    if (prepareUrl) {
                        const effectiveRow = directRow || firstSelectedRow;
                        if (!effectiveRow) return;

                        const prepareLoadingId = notification.showLoading('Preparando...');
                        try {
                            const response = await get(`/?route=${prepareUrl}&rows=${encodeURIComponent(JSON.stringify([effectiveRow]))}`);
                            notification.hideLoading(prepareLoadingId);
                            if (response.status === 200 && response.data?.success) {
                                initialData = response.data.payload;
                            } else {
                                notification.error(response.data?.message || 'Erro ao preparar dados');
                                return;
                            }
                        } catch (error: any) {
                            notification.hideLoading(prepareLoadingId);
                            notification.error(error.message || 'Erro ao preparar dados');
                            return;
                        }
                    } else if (passSelectedRow) {
                        // Visualizadores (logs, históricos) precisam da linha para montar o filtro,
                        // sem ir ao servidor. Opt-in para não pré-popular forms de cadastro.
                        initialData = directRow || firstSelectedRow || null;
                    }

                    const submitUrl = saveRoute
                        ? `/?route=${saveRoute}&action=add`
                        : `/?route=${router}/form&action=add`;

                    const WindowContentWrapper = (props: { data?: any }) => {
                        const handleSubmit = async (formData: any) => {
                            const loadingId = notification.showLoading('Salvando...');
                            try {
                                const response = await post(submitUrl, { dados: formData });
                                notification.hideLoading(loadingId);
                                if (response.status === 200 && response.data?.success) {
                                    windowManager.closeWindow(windowId);
                                    notification.success(response.data?.message || 'Registro criado com sucesso');
                                    refreshData();
                                } else {
                                    notification.error(response.data?.message || 'Erro ao salvar');
                                }
                            } catch (error: any) {
                                notification.hideLoading(loadingId);
                                notification.error(error.message || 'Erro ao salvar');
                            }
                        };

                        return <FormComponent data={initialData} loading={false} triggerSubmit={handleSubmit} triggerSubmitAsNew={handleSubmit} triggerClose={() => windowManager.closeWindow(windowId)} />;
                    };

                    windowId = windowManager.openWindow({
                        title: formTitle || formEntry.title || 'Formulário',
                        component: WindowContentWrapper,
                        size: windowSize,
                        loading: false,
                        data: null
                    });
                } else {
                    const effectiveRow = directRow || firstSelectedRow;
                    if (!effectiveRow) return;

                    const loadingId = notification.showLoading('Carregando...');
                    let editData: any;
                    try {
                        const response = await get(
                            `/?route=${router}/form&action=edit&rows=${encodeURIComponent(JSON.stringify([effectiveRow]))}`
                        );
                        notification.hideLoading(loadingId);
                        if (response.status === 200 && response.data?.success) {
                            editData = response.data.payload;
                        } else {
                            notification.error(response.data?.message || 'Erro ao carregar dados');
                            return;
                        }
                    } catch (error: any) {
                        notification.hideLoading(loadingId);
                        notification.error(error.message || 'Erro ao carregar dados');
                        return;
                    }

                    const WindowContentWrapper = () => {
                        const handleSubmit = async (formData: any) => {
                            const savingId = notification.showLoading('Salvando...');
                            try {
                                const response = await post(`/?route=${router}/form&action=edit`, { dados: formData });
                                notification.hideLoading(savingId);
                                if (response.status === 200 && response.data?.success) {
                                    windowManager.closeWindow(windowId);
                                    notification.success(response.data?.message || 'Salvo com sucesso');
                                    refreshData();
                                } else {
                                    notification.error(response.data?.message || 'Erro ao salvar');
                                }
                            } catch (error: any) {
                                notification.hideLoading(savingId);
                                notification.error(error.message || 'Erro ao salvar');
                            }
                        };

                        const handleSubmitAsNew = async (formData: any) => {
                            const savingId = notification.showLoading('Salvando como novo...');
                            try {
                                const response = await post(`/?route=${router}/form&action=add`, { dados: formData });
                                notification.hideLoading(savingId);
                                if (response.status === 200 && response.data?.success) {
                                    windowManager.closeWindow(windowId);
                                    notification.success(response.data?.message || 'Registro criado com sucesso');
                                    refreshData();
                                } else {
                                    notification.error(response.data?.message || 'Erro ao salvar');
                                }
                            } catch (error: any) {
                                notification.hideLoading(savingId);
                                notification.error(error.message || 'Erro ao salvar');
                            }
                        };

                        return <FormComponent data={editData} loading={false} triggerSubmit={handleSubmit} triggerSubmitAsNew={handleSubmitAsNew} triggerClose={() => windowManager.closeWindow(windowId)} />;
                    };

                    windowId = windowManager.openWindow({
                        title: formTitle || formEntry.title || 'Formulário',
                        component: WindowContentWrapper,
                        size: windowSize,
                        loading: false,
                        data: null
                    });
                }
                return;
            }

            return;
        }

        const { route } = action;
        if (!route) return;
        const { entity, action: actionType } = parseRoute(route);

        if (confirmMessage) {
            const confirmed = await notification.confirm(confirmMessage);
            if (!confirmed) return;
        }

        if (actionType === 'delete') {
            if (selectedRowsArray.length === 0) return;
            const count = selectedRowsArray.length;
            const loadingId = notification.showLoading(
                count === 1 ? 'Excluindo registro...' : `Excluindo ${count} registros...`
            );
            try {
                for (const row of selectedRowsArray) {
                    const response = await post(`/?route=${entity}/form&action=delete`, { dados: [row] });
                    if (response.status !== 200 || !response.data?.success) {
                        throw new Error(response.data?.message || 'Erro ao excluir registro');
                    }
                }
                notification.hideLoading(loadingId);
                notification.success(
                    count === 1 ? 'Registro excluído com sucesso' : `${count} registros excluídos com sucesso`
                );
                refreshData();
                setSelectedRows({});
            } catch (error: any) {
                console.error('Error deleting:', error);
                notification.hideLoading(loadingId);
                notification.error(error.message || 'Erro ao excluir registro(s)');
            }
            return;
        }

        const routeConfig = resolveRoute(route);
        if (!routeConfig) {
            console.warn(`No component resolved for route: ${route}`);
            return;
        }

        const windowSize = resolveWindowSize(
            routeConfig.windowSize
            ?? (routeConfig.type === 'grid' ? 'xl' : 'lg')
        );

        let windowId: string;

        if (actionType === 'add') {
            const WindowContentWrapper = (props: { data?: any }) => {
                const handleSubmit = async (formData: any) => {
                    const loadingId = notification.showLoading('Salvando registro...');
                    try {
                        const response = await post(`/?route=${entity}/form&action=add`, { dados: formData });
                        notification.hideLoading(loadingId);
                        if (response.status === 200 && response.data?.success) {
                            windowManager.closeWindow(windowId);
                            notification.success(response.data?.message || 'Registro criado com sucesso');
                            refreshData();
                        } else {
                            notification.error(response.data?.message || 'Erro ao salvar');
                        }
                    } catch (error: any) {
                        console.error('Erro ao salvar:', error);
                        notification.hideLoading(loadingId);
                        notification.error(error.message || 'Erro ao salvar');
                    }
                };

                const handleClose = () => {
                    windowManager.closeWindow(windowId);
                };

                return <routeConfig.component data={null} loading={false} triggerSubmit={handleSubmit} triggerSubmitAsNew={handleSubmit} triggerClose={handleClose} {...params} />;
            };

            windowId = windowManager.openWindow({
                title: routeConfig.title || 'Formulário',
                component: WindowContentWrapper,
                size: windowSize,
                loading: false,
                data: null
            });
        } else if (actionType === 'edit' || actionType === 'view') {
            const effectiveSelectedRow = directRow || firstSelectedRow;
            if (!effectiveSelectedRow) return;

            let rowData = effectiveSelectedRow;
            if (action.rowFieldsMap) {
                rowData = {};
                Object.entries(action.rowFieldsMap).forEach(([target, source]) => {
                    rowData[target] = effectiveSelectedRow[source];
                });
            } else if (action.rowFields && Array.isArray(action.rowFields)) {
                rowData = {};
                action.rowFields.forEach(field => {
                    rowData[field] = effectiveSelectedRow[field];
                });
            }

            const loadingId = notification.showLoading('Carregando...');
            let editData: any;
            try {
                const response = await get(
                    `/?route=${entity}/form&action=edit&rows=${encodeURIComponent(JSON.stringify([rowData]))}`
                );
                notification.hideLoading(loadingId);
                if (response.status === 200 && response.data?.success) {
                    editData = response.data.payload;
                } else {
                    notification.error(response.data?.message || 'Erro ao carregar dados');
                    return;
                }
            } catch (error: any) {
                notification.hideLoading(loadingId);
                notification.error(error.message || 'Erro ao carregar dados');
                return;
            }

            const WindowContentWrapper = () => {
                const handleSubmit = async (formData: any) => {
                    const savingId = notification.showLoading('Atualizando registro...');
                    try {
                        const response = await post(`/?route=${entity}/form&action=edit`, { dados: formData });
                        notification.hideLoading(savingId);
                        if (response.status === 200 && response.data?.success) {
                            windowManager.closeWindow(windowId);
                            notification.success(response.data?.message || 'Registro atualizado com sucesso');
                            refreshData();
                        } else {
                            notification.error(response.data?.message || 'Erro ao salvar');
                        }
                    } catch (error: any) {
                        notification.hideLoading(savingId);
                        notification.error(error.message || 'Erro ao salvar');
                    }
                };

                const handleSubmitAsNew = async (formData: any) => {
                    const savingId = notification.showLoading('Salvando como novo...');
                    try {
                        const response = await post(`/?route=${entity}/form&action=add`, { dados: formData });
                        notification.hideLoading(savingId);
                        if (response.status === 200 && response.data?.success) {
                            windowManager.closeWindow(windowId);
                            notification.success(response.data?.message || 'Registro criado com sucesso');
                            refreshData();
                        } else {
                            notification.error(response.data?.message || 'Erro ao salvar');
                        }
                    } catch (error: any) {
                        notification.hideLoading(savingId);
                        notification.error(error.message || 'Erro ao salvar');
                    }
                };

                return <routeConfig.component data={editData} loading={false} triggerSubmit={handleSubmit} triggerSubmitAsNew={handleSubmitAsNew} triggerClose={() => windowManager.closeWindow(windowId)} {...params} />;
            };

            windowId = windowManager.openWindow({
                title: routeConfig.title || 'Formulário',
                component: WindowContentWrapper,
                size: windowSize,
                loading: false,
                data: null
            });
        } else if (routeConfig.type === 'grid') {
            const WindowContentWrapper = () => {
                return <routeConfig.component router={entity} selectionMode="single" {...params} />;
            };

            windowId = windowManager.openWindow({
                title: routeConfig.title || 'Grid',
                component: WindowContentWrapper,
                size: windowSize
            });
        }
    }, [selectedRowsArray, firstSelectedRow, windowManager, refreshData]);

    const handleRowDoubleClick = useCallback((row: any) => {
        if (triggerSelect) {
            triggerSelect(row);
            return;
        }

        // O backend pode eleger a action do duplo clique com defaultAction.
        // Sem isso, o padrão continua sendo a action 'edit'.
        const acaoPadrao = allActions.find(action => action.defaultAction)
            || allActions.find(action => {
                if (!action.route) return false;
                try {
                    const { action: actionType } = parseRoute(action.route);
                    return actionType === 'edit';
                } catch {
                    return false;
                }
            });

        if (acaoPadrao) {
            setSelectedRows({ [row.id]: row });
            handleAction(acaoPadrao, row);
        }
    }, [triggerSelect, allActions, handleAction, setSelectedRows]);

    // Cell formatting
    const formatCellValue = useCallback((value: any, field: GridField, row?: any) => {
        if (field.renderComponent && cellRenderers[field.renderComponent]) {
            return cellRenderers[field.renderComponent]({ value, row, field });
        }
        if (value === null || value === undefined) return '';
        if (field.filterList?.[value]) return field.filterList[value];
        if (field.type === 'boolean') return value === true || value === 1 || value === '1' || value === 't' ? 'Sim' : 'Não';
        if (field.type === 'date' && value) return String(value);
        if (field.type === 'datetime' && value) {
            // O backend já envia datetime formatado como d/m/Y H:i:s. Não reparsar com
            // new Date() (que interpreta d/m/Y como m/d/Y e quebra datas com dia <= 12).
            if (typeof value === 'string' && value.includes('/')) return value;
            try {
                const d = new Date(value);
                if (isNaN(d.getTime())) return String(value);
                return d.toLocaleString('pt-BR');
            } catch { return String(value); }
        }
        return String(value);
    }, [cellRenderers]);

    // Larguras das colunas: backend manda px (width > 1) ou flex (width <= 1).
    // Colunas fixas mantêm o px definido; as flex dividem a sobra (mínimo 150px).
    // Se a soma não cabe no wrapper, escala tudo proporcionalmente para nunca gerar overflow.
    const renderedWidths = useMemo(() => {
        const FLEX_MIN = 150;
        const getFixedWidth = (field: GridField) => {
            const width = columnWidths[field.name] ?? field.width;
            return width && width > 1 ? width : null;
        };

        const checkboxWidth = selectionMode !== 'none' ? 32 : 0;
        const availableWidth = wrapperWidth - checkboxWidth;

        const fixedSum = visibleFields.reduce((sum, f) => sum + (getFixedWidth(f) || 0), 0);
        const flexCount = visibleFields.filter(f => getFixedWidth(f) === null).length;
        const flexWidth = flexCount > 0
            ? Math.max(FLEX_MIN, (availableWidth - fixedSum) / flexCount)
            : 0;

        const totalDesired = fixedSum + flexWidth * flexCount;
        const scale = wrapperWidth > 0 && totalDesired > availableWidth ? availableWidth / totalDesired : 1;

        const widths: Record<string, number> = {};
        visibleFields.forEach(f => {
            const desired = getFixedWidth(f) ?? flexWidth;
            widths[f.name] = Math.max(30, Math.floor(desired * scale));
        });
        return widths;
    }, [visibleFields, columnWidths, wrapperWidth, selectionMode]);

    // Column resize
    const handleResizeStart = useCallback((e: React.MouseEvent, fieldName: string, currentWidth: number) => {
        e.preventDefault();
        e.stopPropagation();

        // Teto: a soma das colunas nunca pode ultrapassar a largura do wrapper
        const availableWidth = tableWrapperRef.current?.clientWidth || 0;
        const checkboxWidth = selectionMode !== 'none' ? 32 : 0;
        const reservedOthers = visibleFields.reduce((sum, field) => {
            if (field.name === fieldName) return sum;
            return sum + (renderedWidths[field.name] || 30);
        }, checkboxWidth);
        const maxWidth = Math.max(50, availableWidth - reservedOthers);

        resizingRef.current = { field: fieldName, startX: e.clientX, startWidth: currentWidth };

        const handleMouseMove = (e: MouseEvent) => {
            if (!resizingRef.current) return;
            const diff = e.clientX - resizingRef.current.startX;
            const newWidth = Math.min(maxWidth, Math.max(50, resizingRef.current.startWidth + diff));
            setColumnWidths(prev => ({ ...prev, [resizingRef.current!.field]: newWidth }));
        };

        const handleMouseUp = () => {
            resizingRef.current = null;
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    }, [visibleFields, renderedWidths, selectionMode]);

    return (
        <div className="grid-container">
            <GridFilters
                fields={fields}
                filtrableFields={filtrableFields}
                pendingFilters={pendingFilters}
                addFilter={addFilter}
                updateFilter={updateFilter}
                removeFilter={removeFilter}
                applyFilters={applyFilters}
                handleFilterKeyDown={handleFilterKeyDown}
                lastFilterInputRef={lastFilterInputRef}
                setAppliedFilters={setAppliedFilters}
                setPage={setPage}
                setPageInput={setPageInput}
            />

            <GridActions
                allActions={allActions}
                hideDefaultActions={hideDefaultActions}
                refreshData={refreshData}
                handleAction={handleAction}
                isActionEnabled={isActionEnabled}
                getActionIcon={getActionIcon}
                getActionClass={getActionClass}
                filtrableFields={filtrableFields}
                pendingFilters={pendingFilters}
                addFilter={addFilter}
                selectionMode={selectionMode}
                triggerSelect={triggerSelect}
                firstSelectedRow={firstSelectedRow}
                selectedRowsArray={selectedRowsArray}
            />

            <div className="grid-table-wrapper" ref={tableWrapperRef}>
                <Overlay show={loading} variant="light" animated={false} delay={200} />
                <table className="grid-table">
                    <thead>
                        <tr>
                            {selectionMode !== 'none' && (
                                <th className="grid-th grid-th-checkbox">
                                    {selectionMode === 'multiple' ? (
                                        <input
                                            type="checkbox"
                                            checked={rows.length > 0 && rows.every(row => selectedRows[row.id])}
                                            onChange={handleSelectAll}
                                        />
                                    ) : null}
                                </th>
                            )}
                            {visibleFields.map(field => {
                                const style: React.CSSProperties = { width: renderedWidths[field.name] };
                                return (
                                    <th
                                        key={field.name}
                                        className={`grid-th ${field.cellClass || ''}`}
                                        style={style}
                                    >
                                        <div className="grid-th-content" onClick={() => handleSort(field.name)}>
                                            {field.header}{getSortIndicator(field.name)}
                                            {field.filtrable !== false && (
                                                <button
                                                    className="grid-th-filter-btn"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        addFilter(field.name);
                                                    }}
                                                    title={`Filtrar por ${field.header}`}
                                                >
                                                    <Filter size={12} />
                                                </button>
                                            )}
                                        </div>
                                        <div
                                            className="grid-th-resizer"
                                            onMouseDown={(e) => {
                                                const th = e.currentTarget.parentElement;
                                                const currentWidth = th?.offsetWidth || renderedWidths[field.name] || 50;
                                                handleResizeStart(e, field.name, currentWidth);
                                            }}
                                        />
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length === 0 && !loading ? (
                            <tr><td colSpan={visibleFields.length + (selectionMode !== 'none' ? 1 : 0)} className="grid-empty">Nenhum registro</td></tr>
                        ) : (
                            rows.map((row, idx) => (
                                <GridRow
                                    key={row.id || idx}
                                    row={row}
                                    rowIndex={idx}
                                    visibleFields={visibleFields}
                                    isSelected={!!selectedRows[row.id]}
                                    onRowClick={handleRowClick}
                                    onRowDoubleClick={handleRowDoubleClick}
                                    onCheckboxChange={handleCheckboxChange}
                                    formatCellValue={formatCellValue}
                                    selectionMode={selectionMode}
                                />
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            <GridFooter
                page={page}
                pageInput={pageInput}
                totalPages={totalPages}
                total={total}
                pageSize={pageSize}
                fields={fields}
                visibleColumns={visibleColumns}
                showColumnSelector={showColumnSelector}
                hasSavedState={hasSavedState}
                setPage={setPage}
                setPageInput={setPageInput}
                setPageSize={setPageSize}
                setVisibleColumns={setVisibleColumns}
                setShowColumnSelector={setShowColumnSelector}
                refreshData={refreshData}
                saveState={saveState}
                clearSavedState={clearSavedState}
            />

        </div>
    );
}
